import {
  dinero,
  add as dineroAdd,
  subtract as dineroSubtract,
  multiply as dineroMultiply,
  allocate as dineroAllocate,
  toSnapshot,
} from 'dinero.js'
import type { Dinero } from 'dinero.js'
import { getDineroCurrency } from './constants'

/**
 * Create a dinero instance from minor units and currency code.
 *
 * @param amount - Amount in minor units (e.g. 1250 for $12.50)
 * @param currencyCode - ISO 4217 code (e.g. 'CAD')
 */
export function money(amount: number, currencyCode: string): Dinero<number> {
  return dinero({ amount, currency: getDineroCurrency(currencyCode) })
}

/**
 * Safely add two amounts in the same currency. Returns minor units.
 */
export function addAmounts(a: number, b: number, currencyCode: string): number {
  const currency = getDineroCurrency(currencyCode)
  const result = dineroAdd(
    dinero({ amount: a, currency }),
    dinero({ amount: b, currency })
  )
  return toSnapshot(result).amount
}

/**
 * Safely subtract b from a in the same currency. Returns minor units.
 */
export function subtractAmounts(
  a: number,
  b: number,
  currencyCode: string
): number {
  const currency = getDineroCurrency(currencyCode)
  const result = dineroSubtract(
    dinero({ amount: a, currency }),
    dinero({ amount: b, currency })
  )
  return toSnapshot(result).amount
}

/**
 * Multiply an amount by a factor. Returns minor units.
 *
 * @param amount - Amount in minor units
 * @param factor - Multiplier (e.g. 2 for doubling, 0.1 for tax rate)
 * @param currencyCode - ISO 4217 code
 */
export function multiplyAmount(
  amount: number,
  factor: number,
  currencyCode: string
): number {
  const currency = getDineroCurrency(currencyCode)
  const result = dineroMultiply(dinero({ amount, currency }), factor)
  return toSnapshot(result).amount
}

/**
 * Split an amount into parts according to ratios.
 * Handles remainder distribution (no money is lost).
 *
 * @param amount - Amount in minor units
 * @param currencyCode - ISO 4217 code
 * @param ratios - Array of ratios (e.g. [50, 30, 20] for 50/30/20 split)
 * @returns Array of amounts in minor units
 */
export function allocateAmount(
  amount: number,
  currencyCode: string,
  ratios: number[]
): number[] {
  const currency = getDineroCurrency(currencyCode)
  const parts = dineroAllocate(dinero({ amount, currency }), ratios)
  return parts.map((part) => toSnapshot(part).amount)
}

/**
 * Convert an amount in major units (dollars, as most of the API speaks) to
 * minor units (cents, as `Price` and the helpers above take), using the
 * currency's own exponent.
 *
 * @param amount - Amount in major units (e.g. 12.5 for $12.50)
 * @param currencyCode - ISO 4217 code
 * @returns Amount in minor units (e.g. 1250)
 */
export function majorToMinor(amount: number, currencyCode: string): number {
  const { exponent } = getDineroCurrency(currencyCode)
  return Math.round(amount * Math.pow(10, exponent))
}
